import React, { forwardRef, useState } from 'react';

import { useAuthenticator } from '@aws-amplify/ui-react';
import { Dropdown, Navbar, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';

import avatar from '../../assets/icon-avatar.png';
import useIsMobileScreen from '../../hooks/useCheckIsMobile';
import useReciteMe from '../../hooks/useReciteMe';
import DownloadManualButton from '../DownloadManualButton';
import Modal from '../Modal';
import './styles.scoped.css';
import { useTranslation } from 'react-i18next';

// react-bootstrap's Dropdown.Toggle doesn't forward the scoped-css data attribute to its
// button, so the toggle is rendered through this local component to keep it styleable here.
const ProfileMenuToggle = forwardRef(({ onClick, 'aria-expanded': ariaExpanded, children }, ref) => (
  <button
    type="button"
    ref={ref}
    className="profile-menu-toggle"
    aria-label="profile menu"
    aria-haspopup="true"
    aria-expanded={ariaExpanded}
    onClick={onClick}>
    {children}
  </button>
));

const Header = () => {
  const { t } = useTranslation();
  const { signOut, user } = useAuthenticator(context => [context.signOut]);
  const { authStatus } = useAuthenticator(context => [context.authStatus]);

  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobileScreen();
  const [modalVisible, setModalVisible] = useState(false);
  const isPasswordLess = location.pathname.includes('pw-less') || location.pathname.includes('view-dqf');

  // CP-848 — loads the Recite Me launcher (Centerline only). It renders its own fixed
  // bottom-left button, so nothing is mounted here; this lives in Header because Header is
  // global chrome that mounts once, which is what the launcher's inject-once guard wants.
  useReciteMe();

  const logOut = () => {
    signOut();
    setModalVisible(false);
    navigate('/');
  };

  return (
    <>
      <Navbar className={`navbar ${isMobile && 'mobile'}`}>
        <img
          className="navbar-centerline-logo"
          src={require('../../assets/logo.png')}
          alt="logo"
          height={55}
          onClick={() => navigate('/dashboard')}
        />
        {process.env.REACT_APP_ENV_MESSAGE && (
          <div
            className="message"
            style={{
              fontSize: isMobile ? 12 : '1.8rem',
            }}>
            {process.env.REACT_APP_ENV_MESSAGE}
          </div>
        )}
        <Row className={`header-right ${isMobile && 'mobile'}`}>
          {!!t('header.service_center') && (
            <span style={{ marginRight: authStatus === 'unauthenticated' ? '16px' : null }}>
              {t('header.service_center')}
              <a href={`tel:${t('header.service_center_number')}`}>{t('header.service_center_number')}</a>
              {t('header.service_center_24_hours')}
            </span>
          )}

          {authStatus === 'authenticated' &&
            (isPasswordLess ? (
              <img
                className="avatar-static"
                src={avatar}
                alt="avatar"
                width={isMobile ? 20 : 40}
                height={isMobile ? 20 : 40}
              />
            ) : (
              <Dropdown className="profile-menu">
                <Dropdown.Toggle as={ProfileMenuToggle}>
                  <img src={avatar} alt="avatar" width={isMobile ? 20 : 40} height={isMobile ? 20 : 40} />
                </Dropdown.Toggle>
                {/* renderOnMount so DownloadManualButton fetches once at load, not per menu open */}
                <Dropdown.Menu alignRight renderOnMount>
                  <DownloadManualButton asMenuItem />
                  <Dropdown.Item as="button" onClick={() => setModalVisible(true)}>
                    Logout
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ))}
        </Row>
      </Navbar>
      <Modal
        isOpen={modalVisible}
        title="Sign Out?"
        onClick={logOut}
        secondaryOnClick={() => setModalVisible(false)}
        primaryButtonTitle="Sign Out"
        secondaryButtonTitle="Cancel"
        optionalClose
        bodyContent={false}
      />
    </>
  );
};

export default Header;
